import type {CustomerCalendarQuery} from '../services/customer-calendar.service'
import type {MyClassesQuery} from '../services/my-classes.service'
import type {ClientPaymentsQuery} from '../services/payments.service'
import type {PrivateBookingSlotsQuery} from '../services/private-bookings.service'

export const customerEventsKeys = {
    all: ['customer-events'] as const,
    list: (query: import('../services/events.service').CustomerEventsListQuery) =>
        [...customerEventsKeys.all, 'list', query] as const,
    detail: (eventId: string) => ['customer-events', 'detail', eventId] as const,
    reservationStatus: (eventId: string) => ['customer-events', eventId, 'reservation-status'] as const,
    entryOffer: (eventId: string) => ['customer-events', eventId, 'entry-offer'] as const,
    entryCouponQuotes: (eventId: string) => ['customer-events', eventId, 'entry-coupon-quote'] as const,
    entryCouponQuote: (eventId: string, templateId: string, couponCode: string, benefitCardKey = '') =>
        ['customer-events', eventId, 'entry-coupon-quote', templateId, couponCode, benefitCardKey] as const,
}

export const customerCalendarKeys = {
    all: ['customer-calendar'] as const,
    detail: (query: CustomerCalendarQuery) => [...customerCalendarKeys.all, query] as const
}

export const customerSignupKeys = {
    all: ['customer-signups'] as const,
    detail: (signupId: string) => ['customer-signups', 'detail', signupId] as const,
    history: (signupId: string) => ['customer-signups', signupId, 'history'] as const,
}

export const customerDashboardKeys = {
    all: ['customer-dashboard'] as const,
}

export const customerHomeBootstrapKeys = {
    all: ['customer-home-bootstrap'] as const,
    current: () => [...customerHomeBootstrapKeys.all, 'current'] as const,
}

export const myClassesKeys = {
    all: ['my-classes'] as const,
    list: (params: MyClassesQuery) => [...myClassesKeys.all, params] as const,
    activity: () => [...myClassesKeys.all, 'activity'] as const,
}

export const privateBookingKeys = {
    all: ['private-bookings'] as const,
    offers: () => [...privateBookingKeys.all, 'offers'] as const,
    slots: (offerId: string, params: PrivateBookingSlotsQuery) =>
        [...privateBookingKeys.all, 'slots', offerId, params] as const,
}

export const customerStandingReservationKeys = {
    all: ['customer-standing-reservations'] as const,
    list: () => [...customerStandingReservationKeys.all, 'list'] as const,
}

export const clientPassKeys = {
    all: ['client-passes'] as const,
    accepted: (eventId: string, page: number) => ['client-passes', 'accepted', eventId, page] as const,
    list: () => ['client-passes'] as const,
    detail: (passId: string) => ['client-pass-details', passId] as const,
    events: (passId: string, page: number) => ['client-pass-events', passId, page] as const,
}

export const clientPassTemplateKeys = {
    all: ['client-pass-templates'] as const,
    list: () => ['client-pass-templates'] as const,
    oneTime: () => ['one-time-pass-templates'] as const,
    checkoutPrice: (templateId: string, couponCode?: string | null) => couponCode
        ? ['client-pass-checkout-price', templateId, couponCode] as const
        : ['client-pass-checkout-price', templateId] as const,
}

export const clientPaymentsKeys = {
    all: ['client-payments'] as const,
    list: (query: ClientPaymentsQuery) => ['client-payments', query] as const,
    detail: (orderId: string) => ['client-payment', orderId] as const,
    summary: (orderId: string) => ['client-payment-summary', orderId] as const,
    events: (orderId: string, page: number) => ['client-payment-events', orderId, page] as const,
    transactions: (orderId: string, page: number) => ['client-payment-transactions', orderId, page] as const,
}

export const customerModuleKeys = {
    all: ['customer-modules'] as const,
    capabilities: () => [...customerModuleKeys.all, 'capabilities'] as const,
}

export const clientPenaltyKeys = {
    all: ['client-penalties'] as const,
    activeSummary: () => ['client-penalties', 'active-summary'] as const,
}

export const notificationKeys = {
    all: ['notifications'] as const,
    unreadCount: () => ['notifications', 'unread-count'] as const,
}

export const customerProfileKeys = {
    all: ['customer-profile'] as const,
}

export const customerBusinessDateKeys = {
    all: ['customer-business-date'] as const,
    current: () => [...customerBusinessDateKeys.all, 'current'] as const,
}

export const customerServiceTermsKeys = {
    all: ['customer-service-terms'] as const,
    current: () => ['customer-service-terms', 'current'] as const,
    public: (slug: string) => ['customer-service-terms', 'public', slug] as const,
}

export const partnerStaffAccessKeys = {
    all: ['partner-staff-access'] as const,
    detail: (slug: string, userId: string) =>
        [...partnerStaffAccessKeys.all, slug, userId] as const,
}

export const publicScheduleKeys = {
    week: (slug: string, weekStart: string) => ['public-schedule', slug, 'week', weekStart] as const,
}
